import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  Button,
  IconButton,
  Paper,
  Chip,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { v4 as uuidv4 } from 'uuid';
import CodeEditor from './CodeEditor';
import {
  DEFAULT_STARTER,
  normalizeLanguage,
  normalizeStarterCode,
  resolveStarterCode,
} from './editor/languageTemplates';
import {
  DEFAULT_IDE_EXPERIENCE,
  normalizeIdeExperience,
} from './editor/ideExperienceProfile';
import AttachmentPreviewPanel, { getAttachmentUrl } from './AttachmentPreviewPanel';

export const defaultCodingData = {
  instructions: '',
  challengeAttachment: null,
  keyAttachment: null,
  languages: ['python', 'c', 'cpp', 'java'],
  defaultLanguage: 'python',
  starterCode: { ...DEFAULT_STARTER },
  timeLimitSeconds: 5,
  memoryLimitMb: 256,
  runTimeoutMs: 5000,
  compileTimeoutMs: 10000,
  totalMarks: 10,
  autoGradingEnabled: true,
  allowRun: true,
  allowSubmit: true,
  ideExperience: { ...DEFAULT_IDE_EXPERIENCE },
  sampleTestCases: [{ id: uuidv4(), input: '', expectedOutput: '', description: 'Sample test', visible: true }],
  hiddenTestCases: [{ id: uuidv4(), input: '', expectedOutput: '', description: 'Hidden test', visible: false }],
};

const LANGUAGE_OPTIONS = [
  { id: 'python', label: 'Python' },
  { id: 'c', label: 'C' },
  { id: 'cpp', label: 'C++' },
  { id: 'java', label: 'Java' },
  { id: 'javascript', label: 'JavaScript' },
  { id: 'html', label: 'HTML' },
  { id: 'css', label: 'CSS' },
];

const TestCaseEditor = ({ label, cases, onChange, isHidden = false }) => (
  <Stack spacing={1.5}>
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Typography variant="subtitle2" fontWeight={700}>
        {label}
      </Typography>
      <Button
        size="small"
        startIcon={<AddRoundedIcon />}
        onClick={() =>
          onChange([
            ...cases,
            {
              id: uuidv4(),
              input: '',
              expectedOutput: '',
              description: '',
              visible: !isHidden,
            },
          ])
        }
      >
        Add case
      </Button>
    </Stack>
    {cases.map((testCase, index) => (
      <Paper key={testCase.id || index} variant="outlined" sx={{ p: 1.5 }}>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Chip size="small" label={`Case ${index + 1}`} />
            <IconButton
              size="small"
              color="error"
              disabled={cases.length <= 1}
              onClick={() => onChange(cases.filter((_, i) => i !== index))}
            >
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
          <TextField
            label="Description (optional)"
            size="small"
            fullWidth
            value={testCase.description || ''}
            onChange={(e) => {
              const next = [...cases];
              next[index] = { ...next[index], description: e.target.value };
              onChange(next);
            }}
          />
          <TextField
            label="Input (stdin)"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={testCase.input || ''}
            onChange={(e) => {
              const next = [...cases];
              next[index] = { ...next[index], input: e.target.value };
              onChange(next);
            }}
          />
          <TextField
            label="Expected output"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={testCase.expectedOutput || ''}
            onChange={(e) => {
              const next = [...cases];
              next[index] = { ...next[index], expectedOutput: e.target.value };
              onChange(next);
            }}
          />
        </Stack>
      </Paper>
    ))}
  </Stack>
);

const AttachmentUploadField = ({
  label,
  helperText,
  attachment,
  uploading,
  onUpload,
  onRemove,
}) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
      {label}
    </Typography>
    {helperText && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {helperText}
      </Typography>
    )}
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <Button variant="outlined" component="label" disabled={uploading}>
        {attachment?.fileUrl ? `Replace ${label}` : `Upload ${label}`}
        <input
          type="file"
          accept="application/pdf,image/*,.pdf,.jpg,.jpeg,.png,.gif,.webp"
          hidden
          onChange={onUpload}
        />
      </Button>
      {attachment?.fileUrl && (
        <Button variant="text" color="error" onClick={onRemove}>
          Remove
        </Button>
      )}
    </Stack>
    {getAttachmentUrl(attachment) && (
      <AttachmentPreviewPanel attachment={attachment} />
    )}
  </Paper>
);

export default function CodingProblemBuilder({ value, onChange, attachmentUploading = false, onUploadAttachment }) {
  const data = value || defaultCodingData;
  const starterLanguage = data.defaultLanguage || data.languages?.[0] || 'python';
  const [starterEditLanguage, setStarterEditLanguage] = useState(
    normalizeLanguage(starterLanguage),
  );
  const mergedStarterCode = normalizeStarterCode(data.starterCode || {});
  const ideExperience = normalizeIdeExperience(data.ideExperience);

  useEffect(() => {
    const next = normalizeLanguage(data.defaultLanguage || data.languages?.[0] || 'python');
    setStarterEditLanguage((current) =>
      (data.languages || []).includes(current) ? current : next,
    );
  }, [data.defaultLanguage, data.languages]);

  const update = (patch) => {
    const next = { ...data, ...patch };
    if (patch.starterCode) {
      next.starterCode = normalizeStarterCode(patch.starterCode);
    }
    if (patch.ideExperience) {
      next.ideExperience = normalizeIdeExperience(patch.ideExperience);
    }
    onChange(next);
  };

  const toggleLanguage = (langId, enabled) => {
    const current = Array.isArray(data.languages) ? [...data.languages] : [];
    const next = enabled
      ? Array.from(new Set([...current, langId]))
      : current.filter((l) => l !== langId);
    const nextStarterCode = normalizeStarterCode(data.starterCode || {});
    if (enabled && !nextStarterCode[langId]) {
      nextStarterCode[langId] = resolveStarterCode(langId, nextStarterCode);
    }
    update({
      languages: next.length ? next : [langId],
      defaultLanguage: next.includes(data.defaultLanguage) ? data.defaultLanguage : next[0],
      starterCode: nextStarterCode,
    });
  };

  return (
    <Stack spacing={2}>
      <TextField
        label="Challenge instructions"
        fullWidth
        multiline
        minRows={3}
        value={data.instructions || ''}
        onChange={(e) => update({ instructions: e.target.value })}
      />

      {onUploadAttachment && (
        <Stack spacing={1.5}>
          <AttachmentUploadField
            label="Challenge Image/PDF (optional)"
            helperText="Students see this in a Challenge tab to understand the concept before coding. You can use Excel import instead."
            attachment={data.challengeAttachment}
            uploading={attachmentUploading}
            onUpload={(event) => onUploadAttachment('challengeAttachment', event)}
            onRemove={() => update({ challengeAttachment: null })}
          />
          <AttachmentUploadField
            label="Answer Key Image/PDF (optional)"
            helperText="Shown to students only after they submit their solution."
            attachment={data.keyAttachment}
            uploading={attachmentUploading}
            onUpload={(event) => onUploadAttachment('keyAttachment', event)}
            onRemove={() => update({ keyAttachment: null })}
          />
        </Stack>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
        <TextField
          label="Total marks"
          type="number"
          size="small"
          value={data.totalMarks ?? 10}
          onChange={(e) => update({ totalMarks: Number(e.target.value || 0) })}
          sx={{ minWidth: 140 }}
        />
        <TextField
          label="Run timeout (ms)"
          type="number"
          size="small"
          value={data.runTimeoutMs ?? 5000}
          onChange={(e) => update({ runTimeoutMs: Number(e.target.value || 5000) })}
          sx={{ minWidth: 160 }}
        />
        <TextField
          label="Compile timeout (ms)"
          type="number"
          size="small"
          value={data.compileTimeoutMs ?? 10000}
          onChange={(e) => update({ compileTimeoutMs: Number(e.target.value || 10000) })}
          sx={{ minWidth: 180 }}
        />
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Languages
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {LANGUAGE_OPTIONS.map((lang) => (
            <FormControlLabel
              key={lang.id}
              control={
                <Switch
                  size="small"
                  checked={(data.languages || []).includes(lang.id)}
                  onChange={(e) => toggleLanguage(lang.id, e.target.checked)}
                />
              }
              label={lang.label}
            />
          ))}
        </Stack>
        <TextField
          select
          label="Default language"
          size="small"
          fullWidth
          sx={{ mt: 1 }}
          value={starterLanguage}
          onChange={(e) => update({ defaultLanguage: e.target.value })}
        >
          {(data.languages || ['python']).map((lang) => (
            <MenuItem key={lang} value={lang}>
              {lang.toUpperCase()}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Starter code
        </Typography>
        <TextField
          select
          label="Edit starter for language"
          size="small"
          fullWidth
          sx={{ mb: 1.5 }}
          value={starterEditLanguage}
          onChange={(e) => setStarterEditLanguage(normalizeLanguage(e.target.value))}
        >
          {(data.languages || ['python']).map((lang) => (
            <MenuItem key={lang} value={lang}>
              {lang.toUpperCase()}
            </MenuItem>
          ))}
        </TextField>
        <CodeEditor
          language={starterEditLanguage}
          value={resolveStarterCode(starterEditLanguage, mergedStarterCode)}
          onChange={(code) =>
            update({
              starterCode: {
                ...mergedStarterCode,
                [starterEditLanguage]: code,
              },
            })
          }
          minRows={12}
        />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <FormControlLabel
            control={
              <Switch
                checked={data.autoGradingEnabled !== false}
                onChange={(e) => update({ autoGradingEnabled: e.target.checked })}
              />
            }
            label="Enable auto-grading (falls back to manual review when disabled or runner unavailable)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={data.allowRun !== false}
                onChange={(e) => update({ allowRun: e.target.checked })}
              />
            }
            label="Allow Run (playground)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={data.allowSubmit !== false}
                onChange={(e) => update({ allowSubmit: e.target.checked })}
              />
            }
            label="Allow Submit (graded)"
          />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          IDE experience profile (reusable)
        </Typography>
        <Stack spacing={0.5}>
          <FormControlLabel
            control={
              <Switch
                checked={ideExperience.enableZoomControls}
                onChange={(e) =>
                  update({
                    ideExperience: {
                      ...ideExperience,
                      enableZoomControls: e.target.checked,
                    },
                  })
                }
              />
            }
            label="Enable zoom in/out controls"
          />
          <FormControlLabel
            control={
              <Switch
                checked={ideExperience.enableFullscreenWorkspace}
                onChange={(e) =>
                  update({
                    ideExperience: {
                      ...ideExperience,
                      enableFullscreenWorkspace: e.target.checked,
                    },
                  })
                }
              />
            }
            label="Enable fullscreen coding workspace"
          />
          <FormControlLabel
            control={
              <Switch
                checked={ideExperience.enableTapToFocusKeyboard}
                onChange={(e) =>
                  update({
                    ideExperience: {
                      ...ideExperience,
                      enableTapToFocusKeyboard: e.target.checked,
                    },
                  })
                }
              />
            }
            label="Tap anywhere in editor should open keyboard"
          />
          <FormControlLabel
            control={
              <Switch
                checked={ideExperience.stickyConsoleActionsInFullscreen}
                onChange={(e) =>
                  update({
                    ideExperience: {
                      ...ideExperience,
                      stickyConsoleActionsInFullscreen: e.target.checked,
                    },
                  })
                }
              />
            }
            label="Keep terminal/actions convenient in fullscreen"
          />
          <FormControlLabel
            control={
              <Switch
                checked={ideExperience.compactMobileLayout}
                onChange={(e) =>
                  update({
                    ideExperience: {
                      ...ideExperience,
                      compactMobileLayout: e.target.checked,
                    },
                  })
                }
              />
            }
            label="Compact mobile-friendly editor layout"
          />
        </Stack>
      </Paper>

      <TestCaseEditor
        label="Sample test cases (visible to students)"
        cases={data.sampleTestCases || []}
        onChange={(sampleTestCases) => update({ sampleTestCases })}
      />

      <TestCaseEditor
        label="Hidden test cases (used for auto-grading only)"
        cases={data.hiddenTestCases || []}
        onChange={(hiddenTestCases) => update({ hiddenTestCases })}
        isHidden
      />
    </Stack>
  );
}
